<template>
  <div class="bar-chart">
    <div class="chart-header">
      <h3 class="chart-title">{{ title }}</h3>
      <div v-if="startDate && endDate" class="date-range">
        {{ formatDate(startDate) }} - {{ formatDate(endDate) }}
      </div>
    </div>

    <div v-if="chartData.length > 0" class="chart-container">
      <div class="chart-area">
        <!-- Y-axis labels -->
        <div class="y-axis">
          <div v-for="tick in yAxisTicks" :key="tick" class="y-axis-tick">
            {{ tick }}
          </div>
        </div>

        <!-- Chart content -->
        <div class="chart-content">
          <!-- Chart bars -->
          <div class="bars-container">
            <div v-for="(item, index) in chartData" :key="index" class="bar-wrapper">
              <div
                class="bar"
                :style="{ height: getBarHeight(item.count) + '%' }"
                :title="`${formatDate(item.date)}: ${item.count}`"
              >
                <span v-if="item.count > 0" class="bar-value">{{ item.count }}</span>
              </div>
            </div>
          </div>

          <!-- X-axis labels -->
          <div class="x-axis">
            <div v-for="(item, index) in chartData" :key="index" class="x-axis-label">
              <span class="x-axis-label-text">{{ formatDateShort(item.date) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="no-data">
      <p>No data available for the selected period</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface ChartDataPoint {
  date: Date;
  count: number;
}

interface Props {
  title: string;
  data: { date: number; count: number }[];
  startDate?: Date;
  endDate?: Date;
  color?: string;
}

const props = withDefaults(defineProps<Props>(), {
  color: '#3b82f6',
});

// Convert raw data to chart data with proper dates
const chartData = computed<ChartDataPoint[]>(() => {
  if (!props.data || props.data.length === 0) return [];

  // Group data by date and sum counts for same dates
  const grouped = new Map<string, number>();

  props.data.forEach((item) => {
    const dateKey = new Date(item.date).toDateString();
    grouped.set(dateKey, (grouped.get(dateKey) || 0) + item.count);
  });

  // Convert back to array and sort by date
  return Array.from(grouped.entries())
    .map(([dateStr, count]) => ({
      date: new Date(dateStr),
      count,
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
});

// Calculate maximum count for scaling
const maxCount = computed(() => {
  return Math.max(...chartData.value.map((item) => item.count), 1);
});

// Generate Y-axis ticks
const yAxisTicks = computed(() => {
  const max = maxCount.value;
  const ticks = [];
  const stepSize = Math.max(1, Math.ceil(max / 5));

  for (let i = 0; i <= max; i += stepSize) {
    ticks.push(i); // Keep normal order (0 at bottom)
  }

  return ticks.reverse(); // Reverse for display (highest at top)
});

// Calculate bar height percentage
const getBarHeight = (count: number): number => {
  if (maxCount.value === 0) return 0;
  const percentage = (count / maxCount.value) * 100;
  // Ensure minimum visible height for non-zero values
  return count > 0 ? Math.max(percentage, 3) : 0;
};

// Date formatting functions
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateShort = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
  });
};
</script>

<style scoped>
.bar-chart {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  width: 100%;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.chart-title {
  margin: 0;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 600;
}

.date-range {
  color: #6b7280;
  font-size: 0.875rem;
}

.chart-container {
  width: 100%;
  overflow-x: auto;
  /* overflow-x: auto alone would make overflow-y compute to auto too (CSS
     Overflow spec), so any rotated x-axis label ink that bleeds past its box
     (see .x-axis-label-text) turns into a vertical scrollbar. Pin overflow-y
     to hidden so this container can never grow one, no matter how tight the
     height budget below gets. */
  overflow-y: hidden;
  padding-right: 1rem; /* Add padding to prevent cutoff */
}

.chart-area {
  display: flex;
  min-width: 600px;
  height: 280px;
  width: 100%;
  position: relative;
}

.chart-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 50px;
  padding-right: 1rem;
  height: 200px;
}

.y-axis-tick {
  color: #6b7280;
  font-size: 0.75rem;
  text-align: right;
  line-height: 1;
}

.bars-container {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  border-left: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  padding-left: 0.5rem;
  height: 200px;
  position: relative;
}

.bar-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 30px;
  height: 100%;
  justify-content: flex-end;
}

.bar {
  width: 100%;
  background: v-bind(color);
  border-radius: 2px 2px 0 0;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2px;
}

.bar:hover {
  opacity: 0.8;
  transform: scaleY(1.05);
}

.bar-value {
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  position: absolute;
  top: -20px;
  background: rgba(0, 0, 0, 0.8);
  padding: 2px 6px;
  border-radius: 4px;
  display: none;
}

.bar:hover .bar-value {
  display: block;
}

.x-axis {
  display: flex;
  gap: 4px;
  padding-left: 0.5rem;
  margin-top: 0.75rem;
}

.x-axis-label {
  /* Stays the same width as the bar-wrapper above it (same flex:1 + gap +
     min-width) so labels line up with their bars, but only lays out the
     rotated text as a centered child — the label's own box is never what
     gets rotated. */
  flex: 1;
  display: flex;
  justify-content: center;
  min-width: 30px;
}

.x-axis-label-text {
  /* Sized to its own text (not the flexed slot above), so the -45deg
     rotation's footprint stays small and constant no matter how wide the
     label slot is — that's what keeps it from bleeding past the height
     budget in .chart-area. */
  display: inline-block;
  color: #6b7280;
  font-size: 0.75rem;
  white-space: nowrap;
  transform: rotate(-45deg);
  transform-origin: center;
}

.no-data {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  font-style: italic;
}

.no-data p {
  margin: 0;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .bar-chart {
    padding: 1rem;
    overflow: hidden; /* Prevent any overflow issues */
  }

  .chart-container {
    margin-right: -1rem; /* Compensate for padding */
    padding-right: 2rem; /* Increase padding for mobile */
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .chart-title {
    font-size: 1rem;
  }

  .date-range {
    font-size: 0.8rem;
  }

  .chart-area {
    min-width: 350px; /* Reduce minimum width for mobile */
    height: 235px;
  }

  .y-axis {
    width: 35px; /* Reduce y-axis width */
    padding-right: 0.5rem;
    /* Shrink with .bars-container below — the base 200px plot height left no
       room at all for the x-axis under mobile's shorter chart-area. */
    height: 160px;
  }

  .y-axis-tick {
    font-size: 0.7rem;
  }

  .bars-container {
    gap: 2px; /* Reduce gap between bars */
    height: 160px;
  }

  .bar-wrapper {
    min-width: 20px; /* Reduce minimum bar width */
  }

  .x-axis-label {
    margin-top: 0.25rem;
  }

  .x-axis-label-text {
    font-size: 0.7rem;
  }

  .bar-value {
    font-size: 0.7rem;
    padding: 1px 4px;
  }
}
</style>
